import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Demo configuration - update when deployed
const DEMO_URL = import.meta.env.VITE_ART_SEARCH_DEMO_URL || null;

const ArtSearchDemo = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [demoLoaded, setDemoLoaded] = useState(false);

  // Reset loaded state when URL changes
  useEffect(() => {
    setDemoLoaded(false);
  }, [DEMO_URL]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-glam-accent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <h1 className="text-2xl font-bold text-glam-primary dark:text-glam-light mb-4">
            Art Search Demo
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Sign in to access the interactive demo
          </p>
          <button
            onClick={() => login('google')}
            className="w-full bg-glam-accent hover:bg-glam-accent/90 text-white font-medium py-3 px-4 rounded-lg transition-colors mb-3"
          >
            Sign in with Google
          </button>
          <button
            onClick={() => login('github')}
            className="w-full bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            Sign in with GitHub
          </button>
          <Link
            to="/demos"
            className="block mt-6 text-sm text-glam-accent hover:underline"
          >
            &larr; Back to Demos
          </Link>
        </div>
      </div>
    );
  }

  // If demo URL is not configured, show coming soon
  if (!DEMO_URL) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4 py-4 max-w-7xl">
            <Link
              to="/demos"
              className="text-sm text-glam-accent hover:underline"
            >
              &larr; Back to Demos
            </Link>
          </div>
        </header>

        {/* Coming Soon Content */}
        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-6">🎨</div>
            <h1 className="text-3xl font-bold text-glam-primary dark:text-glam-light mb-4">
              Art Search AI
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
              Search through 116,000+ artworks using natural language or upload an image to find similar pieces.
            </p>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-8">
              <p className="text-amber-800 dark:text-amber-200 text-sm">
                <strong>Coming Soon:</strong> This demo is being prepared for cloud deployment.
                Check back soon or contact us for a private preview.
              </p>
            </div>

            {/* Features Preview */}
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-medium text-glam-primary dark:text-glam-light mb-2">
                  Semantic Search
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Describe what you're looking for in natural language and find matching artworks.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-medium text-glam-primary dark:text-glam-light mb-2">
                  Image Similarity
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Upload an image and discover visually similar artworks from the collection.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-medium text-glam-primary dark:text-glam-light mb-2">
                  3D Gallery
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Browse results in an interactive 3D gallery powered by React Three Fiber.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-medium text-glam-primary dark:text-glam-light mb-2">
                  WikiArt Collection
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Access 116,475 artworks with AI-generated embeddings for discovery.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-block bg-glam-accent hover:bg-glam-accent/90 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Request a Preview
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Demo is deployed - show iframe
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Minimal Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
        <Link
          to="/demos"
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          &larr; Back to Demos
        </Link>
        <span className="text-sm text-gray-500">Art Search AI Demo</span>
      </header>

      {/* Demo Iframe */}
      <div className="flex-1 relative">
        {!demoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-glam-accent mx-auto mb-4"></div>
              <p className="text-gray-400">Loading demo...</p>
            </div>
          </div>
        )}
        <iframe
          src={DEMO_URL}
          className="w-full h-full border-0"
          onLoad={() => setDemoLoaded(true)}
          title="Art Search Demo"
          allow="clipboard-write"
        />
      </div>
    </div>
  );
};

export default ArtSearchDemo;
